import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FormTextField } from '@skupreme/shared/ui/form';
import MarketPlaceService from '@utility/services/marketplace';
import { ToastMessage } from '@utility/toast';
import { ConnectNote, ConnectPopup, failureText } from './ConnectPopup';

const formSchema = z.object({
  account_id: z.string().min(1, 'Account ID is required'),
  consumer_key: z.string().min(1, 'Consumer Key is required'),
  consumer_secret: z.string().min(1, 'Consumer Secret is required'),
  token_id: z.string().min(1, 'Token ID is required'),
  token_secret: z.string().min(1, 'Token Secret is required'),
});

type NetSuiteValues = z.infer<typeof formSchema>;

const EMPTY: NetSuiteValues = {
  account_id: '',
  consumer_key: '',
  consumer_secret: '',
  token_id: '',
  token_secret: '',
};

type NetSuiteConnectPopupProps = {
  open: boolean;
  closeModal: (success?: boolean) => void;
  marketplaceId: string;
  reload: () => void;
};

export function NetSuiteConnectPopup({
  open,
  closeModal,
  marketplaceId,
  reload,
}: NetSuiteConnectPopupProps) {
  const { control, handleSubmit, reset, clearErrors } = useForm<NetSuiteValues>(
    {
      resolver: zodResolver(formSchema),
      defaultValues: EMPTY,
      shouldUnregister: true,
    },
  );

  const [integrating, setIntegrating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const close = (connected?: boolean) => {
    reset(EMPTY);
    clearErrors();
    setSuccess('');
    setError('');
    closeModal(connected);
  };

  const onSubmit = async (data: NetSuiteValues) => {
    try {
      setIntegrating(true);
      setError('');

      await MarketPlaceService.connectNetsuite({
        marketplace_id: marketplaceId,
        account_id: data.account_id.trim(),
        consumer_key: data.consumer_key.trim(),
        consumer_secret: data.consumer_secret.trim(),
        token_id: data.token_id.trim(),
        token_secret: data.token_secret.trim(),
      });

      setSuccess('NetSuite connected successfully');
      reload();
      setTimeout(() => close(true), 2000);
    } catch (failure) {
      const message = failureText(failure, 'message', 'Connection failed');
      setError(message);
      ToastMessage.error(message);
    } finally {
      setIntegrating(false);
    }
  };

  return (
    <ConnectPopup
      open={open}
      title="NetSuite Integration"
      onClose={() => close()}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel={integrating ? 'Connecting...' : 'Connect NetSuite'}
      isSubmitting={integrating}
      error={error}
      success={success}
    >
      <p className="sk:m-0 sk:text-sm sk:font-medium sk:text-fg">
        Enter your NetSuite Token-Based Authentication credentials
      </p>
      <FormTextField
        control={control}
        name="account_id"
        label="Account ID"
        isRequired
      />
      <FormTextField
        control={control}
        name="consumer_key"
        label="Consumer Key"
        isRequired
      />
      <FormTextField
        control={control}
        name="consumer_secret"
        label="Consumer Secret"
        isRequired
      />
      <FormTextField
        control={control}
        name="token_id"
        label="Token ID"
        isRequired
      />
      <FormTextField
        control={control}
        name="token_secret"
        label="Token Secret"
        isRequired
      />
      <ConnectNote>
        These credentials are generated in NetSuite under Setup &rarr;
        Users/Roles &rarr; Access Tokens. The role must have SuiteQL, REST Web
        Services, and Log in using Access Tokens permissions.
      </ConnectNote>
    </ConnectPopup>
  );
}
